import React, { useState } from "react";
import { Button, Card, Form, Input, message, Steps } from "antd";
import { LockOutlined, MailOutlined, SafetyOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { find } from "lodash";
import { Link, useNavigate } from "react-router-dom";
import { dashboard, forgotPassword } from "../../../urls";
import roles, { superAdmin } from "../../../roles";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";
import {
  getMultiFactorResolver,
  sendEmailVerification,
  signInWithEmailAndPassword,
  TotpMultiFactorGenerator,
} from "firebase/auth";
import { auth } from "configs/FirebaseConfig";
import { useCmsUsers } from "../../../data/hooks/firestore/useCmsUsers";

const { Step } = Steps;

export const LoginForm = ({ extra = null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const initialCredentials =
    !process.env.NODE_ENV || process.env.NODE_ENV === "development"
      ? {
          email: process.env.REACT_APP_ADMIN_EMAIL,
          password: process.env.REACT_APP_ADMIN_PW,
        }
      : { email: "", password: "" };

  const [loginEmail, setLoginEmail] = useState(initialCredentials.email);
  const [loginPassword, setLoginPassword] = useState(initialCredentials.password);
  const [totpCode, setTotpCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [mfaResolver, setMfaResolver] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  const { cmsUsers, isLoading: cmsUsersLoading } = useCmsUsers();

  const sendVerificationMail = () => {
    sendEmailVerification(auth.currentUser)
      .then(() =>
        message.success({
          content: t("notifications.invite_link_sent"),
          duration: 8,
        }),
      )
      .catch((err) => message.warning(err, 8));
  };

  const onLogin = async () => {
    setErrorMessage("");
    setIsLoading(true);

    if (cmsUsersLoading) {
      setIsLoading(false);
      message.warning(t("notifications.loading_users"));
      return;
    }

    let cmsUser = find(cmsUsers, (item) => item.userEmail === loginEmail.toLowerCase());

    if (
      !!cmsUser &&
      (cmsUser.userRegion.toLowerCase() === COMPANY_COUNTRY_CODE.toLowerCase() || cmsUser.userRole === superAdmin)
    ) {
      try {
        // Use Firebase v9 auth directly
        const userCredential = await signInWithEmailAndPassword(auth, loginEmail.toLowerCase(), loginPassword);

        if (!userCredential.user.emailVerified) {
          message.warning({
            content: t("notifications.verify_email"),
            duration: 8,
            onClick: sendVerificationMail,
          });
          setIsLoading(false);
        } else {
          // Check if MFA is required but not set up
          const mfaRequired = cmsUser.mfaRequired ?? false;
          const mfaEnabled = cmsUser.totpEnabled ?? false;

          if (mfaRequired && !mfaEnabled && cmsUser.userRole === roles.admin) {
            // MFA is required but not set up - redirect to setup
            message.warning({
              content: t("notifications.mfa_setup_required"),
              duration: 5,
            });
            navigate("/auth/mfa-setup", { state: { mfaRequired: true } });
          } else {
            navigate(dashboard);
          }
        }
      } catch (err) {
        setIsLoading(false);

        // Check if multi-factor authentication is required
        if (err.code === "auth/multi-factor-auth-required") {
          // Create the MFA resolver using the error
          const resolver = getMultiFactorResolver(auth, err);

          setRequiresMfa(true);
          setMfaResolver(resolver);
          setCurrentStep(1);
          message.info(t("notifications.mfa_required"));
        } else {
          message.warning(err.message);
          setErrorMessage(err.code);
        }
      }
    } else {
      setIsLoading(false);
      setErrorMessage(t("notifications.unauthorized_user"));
    }
  };

  const onTotpSubmit = async () => {
    if (!mfaResolver || !totpCode) {
      message.error(t("notifications.enter_totp_code"));
      return;
    }

    if (totpCode.length !== 6) {
      message.error(t("notifications.totp_code_6_digits"));
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      // Find the TOTP factor
      const totpFactor = mfaResolver.hints.find((hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID);

      if (!totpFactor) {
        throw new Error("TOTP factor not found");
      }

      // Create TOTP assertion
      const totpAssertion = TotpMultiFactorGenerator.assertionForSignIn(totpFactor.uid, totpCode);

      // Complete the multi-factor sign-in
      const credential = await mfaResolver.resolveSignIn(totpAssertion);

      // Check email verification
      if (!credential.user.emailVerified) {
        message.warning({
          content: t("notifications.verify_email"),
          duration: 8,
          onClick: sendVerificationMail,
        });
      } else {
        message.success(t("notifications.login_successful"));
        navigate(dashboard);
      }
    } catch (error) {
      console.error("TOTP verification error:", error);
      message.error(t("notifications.invalid_totp_code"));
      setErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetLogin = () => {
    setRequiresMfa(false);
    setMfaResolver(null);
    setCurrentStep(0);
    setTotpCode("");
    setErrorMessage("");
  };

  if (requiresMfa) {
    return (
      <Card>
        <Steps current={currentStep} style={{ marginBottom: "24px" }}>
          <Step title={t("login")} icon={<MailOutlined />} />
          <Step title={t("two_factor_auth")} icon={<SafetyOutlined />} />
        </Steps>

        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <SafetyOutlined style={{ fontSize: "48px", color: "#1890ff", marginBottom: "16px" }} />
          <h3>{t("enter_totp_code")}</h3>
          <p>{t("enter_totp_code_description")}</p>
        </div>

        <Form layout="vertical" onFinish={onTotpSubmit}>
          <Form.Item label={t("totp_code")}>
            <Input
              placeholder={t("enter_6_digit_code")}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))} // Only allow digits
              maxLength={6}
              style={{ textAlign: "center", fontSize: "18px", letterSpacing: "2px" }}
              prefix={<SafetyOutlined className="text-primary" />}
            />
          </Form.Item>

          {errorMessage && (
            <div className="w-100 text-center mb-2">
              <span className="text-danger">{errorMessage}</span>
            </div>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={isLoading} disabled={totpCode.length !== 6}>
              {t("verify_and_login")}
            </Button>
          </Form.Item>

          <Form.Item>
            <Button type="link" onClick={resetLogin} block>
              {t("back_to_login")}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    );
  }

  return (
    <>
      <Form layout="vertical" name="login-form" onFinish={onLogin} className="mt-4" initialValues={initialCredentials}>
        <Form.Item
          name="email"
          label={t("email")}
          onChange={(e) => setLoginEmail(e.target.value)}
          rules={[
            {
              required: true,
              message: t("form.enter_email"),
            },
            {
              type: "email",
              message: t("form.enter_valid_email"),
            },
          ]}
        >
          <Input prefix={<MailOutlined className="text-primary" />} />
        </Form.Item>
        <Form.Item
          name="password"
          onChange={(e) => setLoginPassword(e.target.value)}
          label={t("password")}
          rules={[
            {
              required: true,
              message: t("form.enter_password"),
            },
          ]}
        >
          <Input.Password prefix={<LockOutlined className="text-primary" />} />
        </Form.Item>
        <div className="w-100 text-center mb-2">
          <span className="text-danger">{errorMessage}</span>
        </div>
        <Form.Item>
          <Button type="primary" htmlType="submit" block loading={isLoading}>
            {t("login")}
          </Button>
        </Form.Item>
        <Link to={forgotPassword}>{t("forgot_password")}</Link>
        {extra}
      </Form>
    </>
  );
};

LoginForm.propTypes = {
  extra: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

export default LoginForm;
